import { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import {
  BaseReferenceDto,
  SearchDocument as Document,
} from '@cfa/pathway-fe-shared/xp';
import { getCategoryOptions } from '@cfa/pathway-fe-shared/xp';
import { Typography } from '@cfa/react-components';
import { Accordion } from '@cfa/react-core';

import type {
  UnkeyedCategory,
  UnkeyedDocument,
  UnkeyedSubcategory,
} from '@/hooks/useGetStructuredBrowseData';

import { itemByLanguage } from '@/utils/language';

import PageHeader from '@/components/PageHeader/PageHeader';

import Constants from '@/constants';

import type { IntlCategoryDTO } from '@/services/content-api-types';
import { LanguageObject } from '@/types';

import { ProcedureStep } from '../AddProcedurePopup';
import { PROCEDURE_STEPS } from '../constants';
import { PopupDocument } from '../types';

import DocumentCheckbox from './DocumentCheckbox';

import { IntlSubcategoryDto } from '@/_generated/api-clients/content';
import { userPrefsAtom } from '@/atoms/userPrefs';
import SubcategoryAccordionItem from '@/routes/Explore/Operations/components/SubcategoryAccordionItem';
import SubcategoryDocument from '@/routes/Explore/Operations/components/SubcategoryDocument';

interface Step2Props {
  categorySelected: UnkeyedCategory | null;
  preservedSubcategoryIds: string[];
  setPreservedSubcategoryIds: (ids: string[]) => void;
  subcategories: UnkeyedSubcategory[] | IntlSubcategoryDto[] | null;
  categoryName: string;
  setSubcategories: (
    subcategories: UnkeyedSubcategory[] | IntlSubcategoryDto[] | null,
  ) => void;
  setCategoryName: (name: string) => void;
  setPreviewDocument: (
    doc:
      | Document
      | BaseReferenceDto
      | UnkeyedDocument
      | Record<string, unknown>,
  ) => void;
  setActiveStep: (step: ProcedureStep) => void;
  selectedDocuments: PopupDocument[];
  setSelectedDocuments: (documents: PopupDocument[]) => void;
  selectedCountryCode?: string;
  adminAddingProcedure?: boolean;
  onlyAllowOneDoc?: boolean;
}

const StepTwoSubcategories: React.FC<Step2Props> = ({
  categorySelected,
  preservedSubcategoryIds,
  setPreservedSubcategoryIds,
  subcategories,
  categoryName,
  setSubcategories,
  setCategoryName,
  setPreviewDocument,
  setActiveStep,
  selectedCountryCode,
  adminAddingProcedure = false,
  selectedDocuments,
  setSelectedDocuments,
  onlyAllowOneDoc = false,
}) => {
  const onDocumentClick = (doc: Document) => {
    setPreviewDocument(doc);
    setActiveStep(PROCEDURE_STEPS.PREVIEW);
  };
  const { t } = useTranslation();
  const { language } = useAtomValue(userPrefsAtom);

  const { data } = useQuery({
    ...getCategoryOptions({
      path: { id: categorySelected?.id ?? '' },
      query: { country: selectedCountryCode },
    }),
    enabled: adminAddingProcedure && !!categorySelected?.id,
  });

  // Type assertion needed because the generated SDK returns 'unknown' for this endpoint. Look up getCategory in @cfa/pathway-fe-shared/xp
  const xpCategory = data as IntlCategoryDTO | undefined;

  const handleAccordionChange = (keys: Set<React.Key>) => {
    setPreservedSubcategoryIds([...keys].map(String));
  };

  const resolveName = (
    name: string | LanguageObject | undefined,
    lang: 'en' | 'es' = 'en',
  ): string => {
    if (!name) return '';
    if (typeof name === 'string') return name;
    return name[lang] ?? name.en ?? '';
  };

  const breadcrumbs = [
    { label: t('Browse.categories') },
    { label: categoryName },
  ];

  useEffect(() => {
    if (!adminAddingProcedure) {
      setCategoryName(categorySelected?.name ?? '');
      setSubcategories(
        (categorySelected?.subcategories as UnkeyedSubcategory[] | undefined) ??
          null,
      );
    } else if (xpCategory) {
      if ('name' in xpCategory) {
        setCategoryName(itemByLanguage(xpCategory.name) ?? '');
      }
      setSubcategories(
        (xpCategory.subcategories as UnkeyedSubcategory[] | undefined) ?? [],
      );
    }
  }, [
    adminAddingProcedure,
    categorySelected,
    setCategoryName,
    setSubcategories,
    xpCategory,
  ]);

  return (
    <>
      <PageHeader breadcrumbs={breadcrumbs} title={categoryName} />
      {subcategories && (
        <Accordion.Root
          allowsMultipleExpanded
          expandedKeys={new Set(preservedSubcategoryIds)}
          onExpandedChange={handleAccordionChange}
        >
          {subcategories.map((subcategory, index) => (
            <SubcategoryAccordionItem
              data-testid={`SubcategoryAccordion${index}`}
              id={subcategory.id ?? index}
              key={subcategory.id}
              name={resolveName(subcategory.name)}
            >
              {!!subcategory.subtitles &&
                subcategory.subtitles.map(subtitle => (
                  <div key={subtitle.id}>
                    <Typography variant="overline3">
                      {resolveName(subtitle.name)}
                    </Typography>
                    {subtitle.documents
                      ?.filter(document => {
                        if (!document.id) {
                          datadogRum.addError(
                            new Error('Document missing ID in browse results', {
                              cause: {
                                document,
                                categoryId: categorySelected?.id,
                                categoryName,
                                subcategoryId: subcategory.id,
                                subtitleId: subtitle.id,
                              },
                            }),
                          );
                          return false;
                        }
                        return true;
                      })
                      .map((document, index) => {
                        const newDocument = {
                          id: document.id,
                          type: Constants.STEP_TYPES.DOCUMENT,
                          reference: {
                            id: document.id,
                          },
                          name:
                            typeof document.name === 'string'
                              ? { [language]: document.name }
                              : document.name,
                        } as PopupDocument;
                        return (
                          <div
                            className="flex items-center justify-between"
                            key={`${document.id ?? 'noId'}-${index}-subcat-doc`}
                          >
                            <SubcategoryDocument
                              icon={document.icon}
                              id={document.id ?? ''}
                              name={resolveName(document.name)}
                              onClick={() =>
                                // TODO: Re-look at our typing with DTOs
                                // This cast makes me uncomfortable, as does our interchageablity with ReferenceDTOs and Documents and DocumentDTOs.
                                onDocumentClick(document as Document)
                              }
                            />
                            <DocumentCheckbox
                              documentId={document.id ?? ''}
                              newDocument={newDocument}
                              selectedDocuments={selectedDocuments}
                              setSelectedDocuments={setSelectedDocuments}
                              onlyAllowOneDoc={onlyAllowOneDoc}
                            />
                          </div>
                        );
                      })}
                  </div>
                ))}
            </SubcategoryAccordionItem>
          ))}
        </Accordion.Root>
      )}
    </>
  );
};

export default StepTwoSubcategories;
